import {
  ANALYTICS_ID_ENV_NAMES,
  EVENT_CATEGORIES,
  EVENT_NAMES
} from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { getBootId } from '#~/logger/index.js';
import type { AnalyticsTrackFn } from './types.js';

type LaunchScopedEvent = typeof EVENT_NAMES.BOOT_STARTED | typeof EVENT_NAMES.STUDIO_LOAD_SUCCEEDED;

const LAUNCH_CLAIM_STORAGE_PREFIX = 'analytics.boot-lifecycle.claim';
const claimedInRenderer = new Set<string>();

const readLaunchScope = (): string => {
  const bridgedLaunchId =
    typeof window === 'undefined'
      ? undefined
      : window.electron?.process?.env?.[ANALYTICS_ID_ENV_NAMES.BOOT_ID];

  // Reuse the envelope fallback so the claim and emitted launch_id never diverge.
  return typeof bridgedLaunchId === 'string' && bridgedLaunchId !== ''
    ? bridgedLaunchId
    : getBootId();
};

const claimLaunchScopedEvent = (eventName: LaunchScopedEvent): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const launchScope = readLaunchScope();
  const claim = `${launchScope}:${eventName}`;

  // Memory covers same-document remounts even when browser storage is unavailable.
  if (claimedInRenderer.has(claim)) {
    return false;
  }

  // Mark before tracking so synchronous re-entry cannot emit another canonical/legacy pair.
  claimedInRenderer.add(claim);

  try {
    const storageKey = `${LAUNCH_CLAIM_STORAGE_PREFIX}.${eventName}`;

    // Persist the launch ID so renderer reloads cannot mint another pair for the same launch.
    if (window.sessionStorage.getItem(storageKey) === launchScope) {
      return false;
    }

    window.sessionStorage.setItem(storageKey, launchScope);
  } catch {
    // The in-memory claim still prevents duplicates for the current document.
  }

  return true;
};

// The canonical BOOT_* names replace the legacy APP_BOOT_* trio, while
// STUDIO_LOAD_SUCCEEDED replaces LOADED_STUDIO. All four names are canonical-only.
// Per rule: reference-rules-in-comments (ai.knowledge/global).

export interface BootFailedProperties extends EventProperties {
  boot_duration_ms?: number;
  error_type?: string;
  error_message?: string;
  // ENG-4419: the last boot stage the renderer reached when the boot-deadline
  // watchdog fired boot_failed(boot_timeout). Additive — real error-path
  // boot_failed emits leave it unset, so it is optional here and stays out of
  // boot_failed's required_properties in canonical-event-map.json.
  last_boot_stage?: string;
}

export interface BootSucceededProperties extends EventProperties {
  boot_duration_ms?: number;
}

export type BootStartedProperties = EventProperties;

export interface StudioLoadSucceededProperties extends EventProperties {
  // `| undefined` preserves the property shape used by both the historical
  // loaded_studio rows and the canonical sender when roomId is unresolved.
  studio_id?: string | undefined;
}

export const createBootLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackBootStarted: (properties: BootStartedProperties = {}): void => {
    // Per rule: think-in-systems (ai.knowledge/global) — guard the launch-scoped emit at one source.
    if (!claimLaunchScopedEvent(EVENT_NAMES.BOOT_STARTED)) {
      return;
    }

    // This emitter is canonical-only. Historical legacy rows remain in PostHog for compatible readers.
    track(EVENT_NAMES.BOOT_STARTED, EVENT_CATEGORIES.APP_LIFECYCLE, properties);
  },

  trackBootSucceeded: (properties: BootSucceededProperties): void => {
    // New builds emit only the canonical name. Historical legacy rows remain
    // available to saved readers through the compatibility queries prepared for this cutover.
    track(EVENT_NAMES.BOOT_SUCCEEDED, EVENT_CATEGORIES.APP_LIFECYCLE, properties);
  },

  trackBootFailed: (properties: BootFailedProperties = {}): void => {
    // Compatibility readers retain app_boot_failed history; new failures emit once.
    track(EVENT_NAMES.BOOT_FAILED, EVENT_CATEGORIES.APP_LIFECYCLE, properties);
  },

  // Keep the historical ONBOARDING category so the canonical name preserves its envelope.
  trackStudioLoadSucceeded: (properties: StudioLoadSucceededProperties = {}): void => {
    // Per rule: think-in-systems (ai.knowledge/global) — keep launch dedupe at one source.
    if (!claimLaunchScopedEvent(EVENT_NAMES.STUDIO_LOAD_SUCCEEDED)) {
      return;
    }

    track(EVENT_NAMES.STUDIO_LOAD_SUCCEEDED, EVENT_CATEGORIES.ONBOARDING, properties);
  }
});
